import React from "react";

import { Button } from "@/components/ui/button";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: unknown;
  componentStack?: string;
};

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // This will show up in Lovable console logs and helps us pinpoint the exact cause.
    console.error("[AppErrorBoundary]", error, info);

    // Persist component stack in UI so we can debug even when logs are missing.
    this.setState({
      componentStack: (info as any)?.componentStack,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const err: any = this.state.error;
    const message =
      typeof err?.message === "string" ? err.message : String(this.state.error ?? "Unknown error");
    const stack = typeof err?.stack === "string" ? err.stack : undefined;

    const details = [
      message,
      stack ? `\n\nStack:\n${stack}` : null,
      this.state.componentStack ? `\n\nComponent stack:\n${this.state.componentStack}` : null,
    ]
      .filter(Boolean)
      .join("");

    return (
      <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
        <section className="w-full max-w-lg rounded-2xl border border-border bg-card p-6 shadow-sm">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The app hit an unexpected error. We logged it so we can fix it.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={this.handleReload}>Reload</Button>
            <Button variant="outline" onClick={() => (window.location.href = "/dashboard")}>
              Go to dashboard
            </Button>
          </div>

          <details className="mt-6">
            <summary className="cursor-pointer text-sm text-muted-foreground">Error details</summary>
            <pre className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground">{details}</pre>
          </details>
        </section>
      </main>
    );
  }
}
